import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { publicApi } from '../api';
import { ShoppingBag, Send, ArrowLeft, Tag, Check, X, Instagram } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import FollowInstagramPopup from '../components/FollowInstagramPopup';

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', pincode: '' });
  const [coupon, setCoupon] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(0); // key to force remount
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: f.name || user.name || '', email: f.email || user.email || '' }));
    }
  }, [user]);

  const discount = couponApplied ? couponApplied.discount : 0;
  const discounted = Math.max(0, subtotal - discount);
  // Shipping is based on ORIGINAL subtotal — coupons must not remove free shipping
  const shipping = subtotal === 0 ? 0 : (subtotal >= 499 ? 0 : 49);
  const total = discounted + shipping;

  const applyCoupon = async () => {
    setCouponError('');
    const code = coupon.trim();
    if (!code) return;
    setChecking(true);
    try {
      const res = await publicApi.validateCoupon(code, subtotal, form.email || undefined);
      setCouponApplied({ code: res.code, discount: res.discount, type: res.type, value: res.value });
      toast({ title: `Coupon ${res.code} applied!`, description: `You saved ₹${res.discount}.` });
    } catch (err) {
      setCouponApplied(null);
      setCouponError(err?.response?.data?.detail || 'Invalid coupon');
    } finally {
      setChecking(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCoupon('');
    setCouponError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, title: i.title, price: i.price, qty: i.qty })),
        coupon_code: couponApplied ? couponApplied.code : null,
      };
      const res = await publicApi.createOrder(payload);
      clear();
      navigate(`/order/${res.id}`, { state: { total: res.total, email: form.email } });
    } catch (err) {
      toast({ title: 'Order failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const input = 'w-full px-4 py-3 rounded-2xl border-2 border-[#1a1a1a] font-brico bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]';

  return (
    <div className="bg-[#FFF4E6] min-h-screen">
      <Navbar />
      <section className="px-5 md:px-8 pt-28 pb-16">
        <div className="max-w-6xl mx-auto">
          <Link to="/shop" className="inline-flex items-center gap-2 font-brico font-bold text-sm uppercase tracking-widest opacity-70 hover:opacity-100"><ArrowLeft size={14} /> Back to shop</Link>
          <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] mt-3">Checkout.</h1>

          {items.length === 0 ? (
            <div className="mt-8 bg-white rounded-3xl crunch-shadow p-8 text-center">
              <ShoppingBag size={32} className="mx-auto" />
              <div className="font-display text-2xl uppercase mt-3">Your bowl is empty</div>
              <Link to="/shop" className="btn-chunky primary mt-5 inline-flex">Browse Snacks</Link>
            </div>
          ) : (
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <form onSubmit={submit} className="bg-white rounded-3xl crunch-shadow p-6 md:p-8 space-y-3">
                <div className="font-display text-2xl uppercase">Delivery Details</div>
                <input required placeholder="Full Name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <input required type="email" placeholder="Email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <input required placeholder="Phone" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <textarea required rows={3} placeholder="Address" className={input} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <input required placeholder="City" className={input} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  <input required placeholder="Pincode" className={input} value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
                </div>
                <button disabled={submitting} type="submit" className="btn-chunky primary w-full justify-center">
                  <Send size={16} /> {submitting ? 'Placing order…' : `Place order • ₹${total}`}
                </button>
                <p className="text-xs font-brico opacity-60">We'll get in touch on this email once your order is confirmed.</p>
              </form>

              <div className="bg-[#FFD84D] rounded-3xl crunch-shadow p-6 md:p-8 space-y-3 h-fit">
                <div className="font-display text-2xl uppercase">Your Bowl</div>
                {items.map((it) => (
                  <div key={it.product_id} className="flex items-center gap-3 bg-white/60 rounded-2xl p-3">
                    <div className="w-14 h-14 rounded-xl overflow-hidden"><img src={it.image} alt={it.title} className="w-full h-full object-cover" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase text-lg">{it.title}</div>
                      <div className="text-sm font-brico opacity-70">Qty {it.qty}</div>
                    </div>
                    <div className="font-display">₹{it.price * it.qty}</div>
                  </div>
                ))}

                {/* Coupon */}
                <div className="pt-3 border-t-2 border-[#1a1a1a]/20">
                  <div className="text-xs font-bold uppercase tracking-widest opacity-70 flex items-center gap-2 mb-2"><Tag size={12} /> Promo code</div>
                  {couponApplied ? (
                    <div className="flex items-center justify-between bg-[#2A9D8F] text-white rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2"><Check size={16} /> <span className="font-brico font-bold">{couponApplied.code}</span></div>
                      <div className="flex items-center gap-3">
                        <span className="font-display">−₹{couponApplied.discount}</span>
                        <button type="button" onClick={removeCoupon} className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30"><X size={14} /></button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Have a code?" className="flex-1 px-4 py-3 rounded-2xl border-2 border-[#1a1a1a] font-brico uppercase bg-white focus:outline-none" />
                      <button type="button" onClick={applyCoupon} disabled={checking || !coupon.trim()} className="btn-chunky bg-[#1a1a1a] text-[#FFF4E6] px-4">
                        {checking ? '…' : 'Apply'}
                      </button>
                    </div>
                  )}
                  {couponError && <div className="text-xs text-[#E63946] font-brico font-semibold mt-2">{couponError}</div>}
                </div>

                <div className="pt-3 border-t-2 border-[#1a1a1a]/20 space-y-1">
                  <div className="flex items-center justify-between font-brico"><span>Subtotal</span><span className="font-bold">₹{subtotal}</span></div>
                  {discount > 0 && (
                    <div className="flex items-center justify-between font-brico text-[#2A9D8F]"><span>Discount ({couponApplied.code})</span><span className="font-bold">−₹{discount}</span></div>
                  )}
                  <div className="flex items-center justify-between font-brico"><span>Shipping</span><span className="font-bold">{shipping === 0 ? 'FREE' : `₹${shipping}`}</span></div>
                  <div className="flex items-center justify-between font-display text-2xl uppercase pt-2"><span>Total</span><span>₹{total}</span></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
      <FollowInstagramPopup
        key={showPopup /* re-mounts to reopen when chip is clicked */}
        storageKey="trucrunch_insta_popup_checkout"
        onCodeReady={(code) => { setCoupon(code); }}
      />
      {items.length > 0 && !couponApplied && (
        <button
          onClick={() => {
            // Force reopen even if previously dismissed for this session
            localStorage.removeItem('trucrunch_insta_popup_checkout');
            setShowPopup((n) => n + 1);
          }}
          className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full px-4 py-3 text-white font-brico font-bold text-sm shadow-lg bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:-translate-y-0.5 transition-transform"
        >
          <Instagram size={16} /> Get ₹100 off
        </button>
      )}
    </div>
  );
};

export default Checkout;
